/**
 * t437u: warm up a call's connection before the API's join answer arrives, with LiveKit's room.prepareConnection(url):
 * DNS, TCP and TLS to the LiveKit host are done while the call rings (or while the person sheet / chat with a Call
 * button is open), so room.connect() starts on a warm connection instead of paying for them after Answer / Call.
 *
 * The LiveKit URL comes from GET /me (`livekit_url`), so no token is needed: on a self-hosted server prepareConnection
 * only uses the token to pick a LiveKit Cloud region. Fire and forget: prepare() never throws or rejects, failures are
 * logged at debug level, and the call itself never waits for it. Dependency free so the web and the phone app share it
 * (each passes its own livekit-client Room factory).
 */
export interface PreparableRoom { prepareConnection(url: string, token?: string): Promise<void> }

export interface CallPreparerOptions {
  /** A Room (or anything with prepareConnection) to warm with; made once, on the first prepare. */
  room: () => PreparableRoom | Promise<PreparableRoom>;
  /** Don't warm the same URL again within this window (a warm TLS connection / DNS entry outlives it). */
  minIntervalMs?: number;
  /** Debug logging for failures and what was warmed. */
  debug?: (msg: string, err?: unknown) => void;
  now?: () => number;
}

export interface CallPreparer {
  /** The LiveKit URL to warm when prepare() is called without one (from /me). */
  setUrl(url: string | null | undefined): void;
  url(): string | null;
  /** Warm the connection to `url` (or the known one). Resolves once done; never rejects; safe to ignore. */
  prepare(url?: string | null, token?: string): Promise<boolean>;
}

/** wss://host/path → the same thing the SDK fetches (https://host/path); null for anything that isn't a LiveKit URL. */
export function normaliseLiveKitUrl(url: string | null | undefined): string | null {
  if (!url || typeof url !== 'string') return null;
  try {
    const u = new URL(url.trim());
    return ['wss:', 'ws:', 'https:', 'http:'].includes(u.protocol) && u.hostname ? u.toString() : null;
  } catch { return null; }
}

export function createCallPreparer(o: CallPreparerOptions): CallPreparer {
  const minInterval = o.minIntervalMs ?? 20_000;
  const now = o.now ?? Date.now;
  const debug = (msg: string, err?: unknown) => { try { o.debug?.(msg, err); } catch { /* logging never breaks a call */ } };
  let known: string | null = null;
  let room: Promise<PreparableRoom> | null = null;
  const last = new Map<string, number>();
  const inFlight = new Map<string, Promise<boolean>>();

  const getRoom = () => {
    if (!room) {
      room = Promise.resolve().then(o.room);
      room.catch(() => { room = null; }); // try again next time
    }
    return room;
  };

  return {
    setUrl(url) { known = normaliseLiveKitUrl(url); },
    url: () => known,
    prepare(url, token) {
      try {
        const target = normaliseLiveKitUrl(url ?? known);
        if (!target) return Promise.resolve(false);
        const running = inFlight.get(target);
        if (running) return running;
        const at = last.get(target);
        if (at !== undefined && now() - at < minInterval) return Promise.resolve(false);
        last.set(target, now());
        const p = getRoom()
          .then(r => r.prepareConnection(target, token))
          .then(() => { debug(`call: prepared ${target}`); return true; })
          .catch(e => { last.delete(target); debug('call: prepareConnection failed (ignored)', e); return false; })
          .finally(() => { inFlight.delete(target); });
        inFlight.set(target, p);
        return p;
      } catch (e) {
        debug('call: prepare failed (ignored)', e);
        return Promise.resolve(false);
      }
    },
  };
}
